import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Alert from '@mui/material/Alert/Alert';
import AlertTitle from '@mui/material/AlertTitle/AlertTitle';
import { PageContainer } from '../../components/PageContainer';
import { Loading } from '../../components/Loading';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { PlanCards } from '../../components/Billing/PlanCards';
import { useHackathon } from '../../hooks/useHackathon';
import { useAuth } from '../../services/useAuth';
import { BillingStatus, confirmCheckout, formatPrice, getBilling, startCheckout } from '../../services/billing.service';
import { formatDate } from '../../utils/hackathonStatus';

/**
 * Upgrading one hackathon to Pro: the plans, Stripe Checkout, and the confirmation when the organizer comes back.
 */
export const UpgradePage = () => {
    const { id } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { hackathon, loading, reload } = useHackathon(id);
    const [billing, setBilling] = useState<BillingStatus | null>(null);
    const [error, setError] = useState('');
    const [redirecting, setRedirecting] = useState(false);
    const [justUpgraded, setJustUpgraded] = useState(false);
    const confirming = useRef(false);

    const sessionId = params.get('session_id');
    const canceled = params.get('canceled') === '1';

    useEffect(() => {
        if (!id) {
            return;
        }
        // back from Stripe: confirm that session first (the page then drops the query, so a reload doesn't redo it)
        if (sessionId) {
            if (confirming.current) {
                return;
            }
            confirming.current = true;
            confirmCheckout(id, sessionId).then(status => {
                setBilling(status);
                if (status.plan === 'pro') {
                    setJustUpgraded(true);
                    // the hackathon (and its branding) changed: refetch it so every page shows the new look
                    reload?.();
                }
                navigate(`/h/${id}/upgrade`, { replace: true });
            }, err => setError(err?.response?.data?.message || 'Could not confirm the payment'));
            return;
        }
        getBilling(id).then(setBilling, err => setError(err?.response?.data?.message || 'Could not load this event\'s plan'));
    }, [id, sessionId]);

    if (loading && !hackathon) {
        return <Loading />;
    }
    if (!hackathon || !id) {
        return <PageContainer>No hackathon found</PageContainer>;
    }
    if (!user || !hackathon.organizers.some(o => o.email === user.email)) {
        return <PageContainer>Only the hackathon's organizers can change its plan.</PageContainer>;
    }

    const upgrade = () => {
        setRedirecting(true);
        setError('');
        startCheckout(id).then(url => {
            window.location.assign(url);
        }, err => {
            setRedirecting(false);
            setError(err?.response?.data?.message || 'Could not start checkout');
        });
    };

    const pricing = billing?.pricing;
    const paid = billing?.purchases[0];

    return (
        <PageContainer>
            <Stack spacing={3} sx={{ maxWidth: 960, mx: 'auto', width: '100%' }}>
                <AppBreadcrumbs crumbs={[
                    { type: PageType.Home, link: '/', name: 'Home' },
                    { type: PageType.Hackathon, link: `/h/${hackathon.slug}`, name: hackathon.name },
                    { type: PageType.Organizer, link: `/h/${hackathon.slug}/organizer`, name: 'Organize' },
                    { type: PageType.Article, name: 'Plan' }
                ]} />
                <Stack spacing={1}>
                    <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>{billing?.plan === 'pro' ? 'This is a Pro event' : 'Upgrade to a Pro event'}</Typography>
                    <Typography color="text.secondary">{hackathon.name}</Typography>
                </Stack>

                {canceled && !billing?.purchases.length && <Alert severity="info">Checkout was canceled; nothing was charged.</Alert>}
                {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                {sessionId && !billing && !error && <Stack spacing={1} alignItems="center"><Loading /><Typography>Confirming your payment…</Typography></Stack>}

                {
                    billing?.plan === 'pro' && (
                        <Alert severity="success" action={
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button color="inherit" size="small" component={Link} to={`/h/${hackathon.slug}/edit#branding`}>Set up branding</Button>
                                <Button color="inherit" size="small" component={Link} to={`/h/${hackathon.slug}`}>See the event</Button>
                            </Stack>
                        }>
                            <AlertTitle>{justUpgraded ? 'Payment received. This event is Pro.' : 'Pro event'}</AlertTitle>
                            Your branding now shows to everyone in this hackathon.
                            {paid && <> Paid {formatPrice(paid.amount, paid.currency)}{paid.paidBy?.username ? ` by ${paid.paidBy.username}` : ''} on {formatDate(paid.paidAt, 'MMM D, YYYY')}.</>}
                            {!paid && billing.granted && <> Pro was turned on for this event by Persera{billing.planUpgradedAt ? ` on ${formatDate(billing.planUpgradedAt, 'MMM D, YYYY')}` : ''}, at no charge.</>}
                        </Alert>
                    )
                }

                {
                    pricing && (
                        <PlanCards pricing={pricing} current={billing?.plan} proAction={
                            billing?.plan === 'pro' ? null : (
                                <Stack spacing={1}>
                                    <Button variant="contained" size="large" disabled={redirecting || !pricing.available} onClick={upgrade}>
                                        {redirecting ? 'Opening checkout…' : `Upgrade this event for ${formatPrice(pricing.proEventPrice, pricing.currency)}`}
                                    </Button>
                                    {!pricing.available && <Typography variant="body2" color="error">Payments aren't available on this server.</Typography>}
                                    <Typography variant="body2" color="text.secondary">
                                        You can set up branding now in <Link to={`/h/${hackathon.slug}/edit#branding`}>Edit hackathon</Link> and preview it there; participants see it once the event is Pro.
                                    </Typography>
                                </Stack>
                            )
                        } />
                    )
                }
            </Stack>
        </PageContainer>
    );
}
